// ─── Archetypes ───────────────────────────────────────────

export type Archetype = 'siege_commander' | 'swarm_tactician' | 'arcane_scholar'

// ─── Relic Engine Hook Effects ────────────────────────────

/** Periodic effect applied each engine tick (e.g. heal all player units every N ms). */
export type TickEffect = {
  type: 'healPlayerUnits'
  amount: number
  intervalMs: number
  timer: number
}

/** Effect applied each time the player plays a card. */
export type OnCardPlayedEffect = {
  attackBonus: number
}

// ─── Structure & Upgrade Effects ─────────────────────────

export type StructureEffect =
  | { type: 'mana'; amount: number }
  | { type: 'spawn'; unitTemplate: UnitTemplate; intervalMs: number }
  | { type: 'manaSpeed'; speedMult: number }
  | { type: 'healAura'; amount: number; intervalMs: number }
  | { type: 'repairAura'; amount: number; intervalMs: number }
  | { type: 'attackAura'; amount: number }
  | { type: 'slowZone'; slowFactor: number; radius: number; damagePerSec?: number }

export type UpgradeEffect =
  | { type: 'buffAttack'; amount: number }
  | { type: 'healUnits';  amount: number }
  | { type: 'buffSpeed';  amount: number }         // adds to moveSpeed of all mobile units
  | { type: 'buffMaxHp';  amount: number }         // increases maxHp and heals by same amount
  | { type: 'buffRange';  amount: number }         // increases attackRange of all attacking units
  | { type: 'aoe'; damage?: number; range?: number; amount?: number }  // AOE damage: range-limited or global
  | { type: 'buffHp'; amount: number }             // increases current HP of all friendly units (not maxHp)
  | { type: 'buffAttackCooldown'; amount: number } // adjusts attackCooldownMs for all friendly units (negative = faster)
  | { type: 'buffHeal'; amount: number }           // one-time heal of all friendly units

// ─── Cards ───────────────────────────────────────────────

export type CardRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic' | 'shiny' | 'holofoil' | 'glass'
/** Rarities that are secret — hidden in collections until obtained. */
export const SECRET_RARITIES: ReadonlySet<CardRarity> = new Set(['mythic', 'shiny', 'holofoil', 'glass'])
export type CardType = 'unit' | 'structure' | 'upgrade' | 'augment'

// ─── Augment System ───────────────────────────────────────

/** Which equipment slot an augment card occupies. */
export type AugmentSlot = 'primaryRanged' | 'heavyMelee' | 'helmet' | 'chest' | 'arms' | 'legs' | 'amulet'

/** Flat stat deltas granted by an equipped augment (scaled by upgrade level). */
export interface AugmentEffect {
  attack?: number
  attackRange?: number
  maxHp?: number
  moveSpeed?: number
}

/** One owned copy of an augment card — each instance is independently levelled and equippable. */
export interface AugmentInstance {
  instanceId: string
  cardId: string            // augment card name (e.g. "Thunder Helm")
  level: number             // 0 = base; each upgrade adds +1
  equippedToCardName: string | null   // unit card name this is equipped on, or null
}

export interface UnitTemplate {
  name: string
  attack: number
  maxHp: number
  isWall: boolean
  /** True for moat terrain — indestructible, untargetable, slows units that cross it. */
  isMoat?: boolean
  /** True if this unit skips walls when choosing attack targets (ranged / magic). */
  bypassWall: boolean
  /** True if this unit physically flies over walls — never stopped by them. */
  flying?: boolean
  /** True if this unit can scale walls — passes through at 25 % speed instead of stopping. */
  climber?: boolean
  moveSpeed: number          // pixels per second (0 = stationary structure)
  attackRange: number        // pixels — distance at which unit can attack
  attackCooldownMs: number   // ms between attacks
  structureEffect?: StructureEffect
  /** Descriptive tags used for strength/weakness matching. */
  tags?: UnitTag[]
  /** Tags of unit types this unit deals ×1.5 damage to. */
  strengths?: UnitTag[]
  /** Tags of unit types that deal ×1.5 damage to this unit. */
  weaknesses?: UnitTag[]
  /** Biases target selection away from default nearest-enemy behaviour. */
  targetPriority?: TargetPriority
  /** Restricts which unit types this unit can attack. */
  targetUnitType?: 'flying' | 'hero' | 'not_flying'
  /** Proximity buff triggered when a named ally is within range. */
  affinity?: AffinityDef
  /** Hidden behavioural trait — flee, guard, or priority movement. */
  unitTrait?: UnitTrait
  /** Flavour text shown in the inspect panel. */
  lore?: string
  /** Visual size of the unit sprite on the battlefield. Defaults to 'medium'. */
  size?: 'small' | 'medium' | 'large'
  /** Secret-rarity visual tag — applied to units spawned from shiny/holofoil/glass/mythic cards. */
  cardVariant?: 'shiny' | 'holofoil' | 'glass' | 'mythic'
  /** Mastery level of this card from the player's collection (0 = unmastered). */
  masteryLevel?: number
  /** AOE explosion triggered once when this unit first drops to ≤50% HP. */
  halfHealthEffect?: { damage: number; range: number }
  /** AOE explosion triggered when this unit dies. */
  onDeathEffect?: { damage: number; range: number }
  /** Periodic forward blink — unit teleports ahead every cooldownMs ms. */
  teleportAbility?: { cooldownMs: number; distancePx: number }
  /** Periodic invisibility — unit becomes untargetable for activeMs, then enters cooldownMs cooldown. */
  invisibilityAbility?: { activeMs: number; cooldownMs: number }
  /** Blood pool consumption — unit raises a minion from a nearby blood pool every cooldownMs. */
  bloodSummonAbility?: { cooldownMs: number; minionTemplate: UnitTemplate; range: number }
  /** Elemental on-hit effect applied when this unit attacks. Auto-derived from tags when not set. */
  attackEffect?: AttackEffect
  /** Signature hero ability. Exactly one key is set per hero — see `HeroAbilities`. */
  heroAbility?: HeroAbilities
}

/**
 * Signature abilities for the campaign-2 heroes. Each hero card sets exactly one
 * key here, and each key is a mechanic that exists nowhere else in the engine —
 * the point is that no two heroes play the same, and none of them replays a
 * chapter-1 trick (`teleportAbility`, `invisibilityAbility`, `bloodSummonAbility`,
 * `halfHealthEffect`, `onDeathEffect`, the `structureEffect` auras).
 *
 * All of them are driven from `engine/heroAbilities.ts`: the periodic ones off a
 * single shared `heroAbilityTimer`, the reactive ones off hooks in `combat.ts`,
 * `units.ts`, `targeting.ts` and `engine/cards.ts`.
 */
export interface HeroAbilities {
  /** Bulwark — soaks a share of every incoming hit and reflects part of it back at a melee attacker. */
  bulwark?: { damageReductionPct: number; thornsPct: number }
  /** Safe Passage — allies in range ignore slow zones, moat drag and ground hazards, and move faster. */
  safePassage?: { range: number; speedBonus: number }
  /** Harvest — every kill permanently raises this unit's own attack, up to maxStacks. */
  harvest?: { attackPerKill: number; maxStacks: number }
  /** Cross-Reference — periodically marks the strongest enemy in range; all allies hit it harder. */
  crossReference?: { cooldownMs: number; range: number; bonusDamagePct: number; durationMs: number }
  /** Last Light — when an ally dies in range, surviving allies are healed and gain a timed attack surge. */
  lastLight?: { range: number; healAmount: number; attackBonus: number; durationMs: number }
  /** Undertow — periodically drags every enemy in range back toward their own base. */
  undertow?: { cooldownMs: number; range: number; pullPx: number }
  /** Mirror Step — periodically spawns a short-lived reflection that enemies target instead. */
  mirrorStep?: { cooldownMs: number; decoyHpPct: number; durationMs: number }
  /** Duel — always challenges the highest-attack enemy in range and hits it harder. */
  duel?: { bonusDamagePct: number }
  /** Cold Snap — periodic pulse that slows enemy movement AND attack speed in an area. */
  coldSnap?: { cooldownMs: number; range: number; slowFactor: number; attackSlowPct: number; durationMs: number }
  /** Deeproot — regenerates HP while it has not been hit recently, and cannot be slowed or displaced. */
  deeproot?: { hpPerSec: number; calmMs: number }
  /** Forced March — friendly units deployed while this unit lives arrive further forward and faster. */
  forcedMarch?: { advancePx: number; speedBonus: number }
  /** Breach — hits knock the target backwards, and walls/structures take multiplied damage. */
  breach?: { knockbackPx: number; structureDamageMult: number }
  /** Unbroken Vigil — negates a lethal blow on a nearby ally, leaving it on 1 HP. Limited charges. */
  vigil?: { range: number; charges: number }
}

export type BuffTag = 'atk' | 'spd' | 'hp' | 'range'

export type UnitTag = 'flying' | 'ranged' | 'melee' | 'fast' | 'slow' | 'large' | 'magic' | 'undead' | 'beast' | 'armored' | 'siege' | 'fire' | 'swim' | 'burrowing' | 'ember' | 'frost' | 'glacier' | 'lightning' | 'poison' | 'aoe' | 'gascloud' | 'boss'

/** Visual style of a projectile — drives rendering in both the battle and TD renderers. */
export type ProjectileType = 'arrow' | 'fireball' | 'icebolt' | 'poisonblob' | 'magic' | 'lightning' | 'aoe'

/** Derive a projectile visual type from a unit's tags. */
export function getProjectileType(tags: UnitTag[] | undefined): ProjectileType {
  if (!tags) return 'magic'
  if (tags.includes('fire') || tags.includes('ember'))          return 'fireball'
  if (tags.includes('frost') || tags.includes('glacier'))       return 'icebolt'
  if (tags.includes('poison') || tags.includes('gascloud'))     return 'poisonblob'
  if (tags.includes('lightning') || tags.includes('aoe'))       return 'lightning'
  if (tags.includes('ranged'))                                   return 'arrow'
  return 'magic'
}

/** Elemental on-hit effect applied by units with matching tags (fire→burn, frost→freeze, etc.). */
export interface AttackEffect {
  type: 'burn' | 'freeze' | 'poison' | 'shock' | 'aoe' | 'gascloud'
  /** 0–1 probability per hit. */
  chance: number
  /** Duration in ms — for burn/poison: DoT duration; for freeze: slow duration; for gascloud: cloud lifetime. */
  durationMs: number
  /** Damage per second — for burn, poison, and gascloud. */
  dps?: number
  /** 0–1 speed multiplier while frozen — 0 = complete stop. */
  slowFactor?: number
  /** Pixel radius — for aoe: splash radius; for gascloud: cloud radius on the map. */
  aoeRadius?: number
}
export type TargetPriority = 'walls' | 'buildings' | 'boss' | 'ranged_first'

export interface AffinityDef {
  withName: string        // name of ally unit that triggers the affinity
  range: number           // px proximity to activate
  effectType: 'attackSpeed' | 'damage' | 'moveSpeed'
  effectAmount: number    // multiplier (e.g. 1.3 = +30%; 0.8 = −20% incoming damage)
  label: string           // e.g. "Archer's Tempo"
}

export interface UnitTrait {
  /** Tags of enemy units this unit flees from when within fleeRange px. */
  fleeFrom?: UnitTag[]
  /** Flee trigger distance in px (default 80). */
  fleeRange?: number
  /** If true: hang back near own base; only engage if an enemy comes within engageRange px. */
  guardBase?: boolean
  /** How far from own base this unit patrols when guarding (default 80 px). */
  baseGuardRange?: number
  /** Enemy proximity to own base that breaks the guard stance (default 180 px). */
  engageRange?: number
  /** Builder mode — unit moves to nearest friendly building, repairing and upgrading it. */
  builderMode?: boolean
  /** ms between builder repair/upgrade ticks (default 3000). */
  buildIntervalMs?: number
  /** HP healed to a damaged building per builder tick (default 8). */
  buildRepairAmount?: number
  /** Total number of repair/upgrade charges before entering saboteur mode (default 3). */
  buildCharges?: number
}

export interface Unit extends UnitTemplate {
  id: string
  owner: 'player' | 'opponent'
  hp: number
  x: number                  // forward axis: 0=player base, LANE_WIDTH=opponent base
  y: number                  // lateral axis: one of [-80,-40,0,40,80] (5 lanes, continuous during movement)
  attackTimer: number        // ms until this unit can attack again
  spawnTimer?: number        // ms until next spawn (spawner buildings only)
  upgradeLevel?: number      // 1 = base, 2+ = upgraded (structures only)
  spawnGrowTimer?: number    // ms remaining in grow-in animation (building spawns only)
  buffs?: BuffTag[]          // active buff tags for UI display
  isHero?: boolean           // true for units deployed from hero cards
  spriteName?: string        // sprite lookup override — hero units retain their base unit's sprite name
  affinityActive?: boolean   // runtime: affinity buff currently in effect
  /** True while unit is passing through an enemy wall zone (enables climb animation). */
  climbing?: boolean
  /** ms remaining in death animation — unit lingers on field while > 0. */
  dyingTimer?: number
  /** ms remaining in damage-flash animation. */
  damageFlashTimer?: number
  /** ms remaining in kill-flash animation (brief glow when this unit lands a kill). */
  killFlashTimer?: number
  /** ms remaining stunned — unit cannot move or attack while > 0 (boss trait effect). */
  stunTimer?: number
  /** True for commander units — the base avatar that fights on the field. */
  isCommander?: boolean
  /** X position the commander is leashed to; cannot wander beyond COMMANDER_LEASH_PX from here. */
  commanderHomeX?: number
  /** Persistent random y position assigned to a guardBase unit (lazy, set on first guard tick). */
  guardY?: number
  /** Ordered remaining road waypoints for road-following movement (lazy, computed on first
   *  movement tick when the battle has `roadFollowing` enabled). Empty once the unit has
   *  passed the last waypoint or no road was found; undefined until first computed. */
  roadWaypoints?: Array<{ x: number; y: number }>
  /** Lateral lane this unit prefers while advancing, picked at random on its
   *  first movement tick (lazy — spawnUnit has no GameState access). Used as the
   *  default target's `y` instead of dead centre, so an army fans out across the
   *  battlefield rather than collapsing onto the centre line and then all
   *  detouring round terrain on the same side. Overridden by anything with a
   *  real target: road waypoints, enemy chasing, defend slots. */
  advanceY?: number
  /** Flow-field distance-to-goal recorded when this unit started routing around
   *  terrain (hard-blocking battles only — see moveUnits). While set, the unit
   *  keeps following the flow field even on ticks when the direct path looks
   *  open again, and only resumes normal movement once it has actually gotten
   *  closer to the goal than it was when it got stuck. Without that commitment
   *  a unit oscillates forever at a concave obstacle: the route around requires
   *  briefly moving AWAY from the goal, which instantly makes the direct path
   *  look viable again, so it turns back into the wall. Undefined = not
   *  detouring. */
  detourFlowDist?: number
  /** ID of the enemy unit this unit is currently locked onto as an attack target. */
  targetId?: string
  /** ID of the last enemy unit that dealt damage to this unit (used for target-switch on hit). */
  lastAttackerId?: string
  /** True once the halfHealthEffect has fired — prevents re-triggering. */
  halfHealthFired?: boolean
  /** ms until next teleport blink. */
  teleportTimer?: number
  /** ms remaining invisible (> 0 = invisible; enemies cannot target this unit). */
  invisTimer?: number
  /** ms remaining in invisibility cooldown before the next active phase. */
  invisCooldownTimer?: number
  /** ms until next blood pool summon attempt. */
  bloodSummonTimer?: number
  /** ms until next builder repair/upgrade tick. */
  buildTimer?: number
  /** Remaining charges before the builder enters saboteur mode. */
  builderChargesLeft?: number
  /** True once all charges are spent — builder runs to enemy base to self-destruct. */
  builderSaboteurMode?: boolean
  /** ID of the last building the builder serviced — excluded when picking next target. */
  builderLastBuildingId?: string
  /** ms remaining burning — unit takes burnDps damage per second while > 0. */
  burnTimer?: number
  burnDps?: number
  burnAccum?: number
  /** ms remaining frozen — unit's move speed is multiplied by freezeSlow while > 0. */
  freezeTimer?: number
  freezeSlow?: number
  /** ms remaining poisoned — unit takes poisonDps damage per second while > 0. */
  poisonTimer?: number
  poisonDps?: number
  poisonAccum?: number
  /** ms until the next moat damage pulse — prevents continuous per-frame chip damage. */
  moatDamageTimer?: number
  /** True once this unit's Phantom Legion revive chance has been rolled (one roll per death). */
  reviveRolled?: boolean

  // ─── Hero ability runtime state (see HeroAbilities / engine/heroAbilities.ts) ───
  /** ms until this unit's periodic hero ability fires again. One timer is enough:
   *  a hero carries at most one periodic ability. */
  heroAbilityTimer?: number
  /** Harvest: attack permanently gained from kills so far. */
  harvestStacks?: number
  /** Cross-Reference: absolute game-time this unit stops being marked. */
  markedUntil?: number
  /** Cross-Reference: bonus damage multiplier applied to hits on this unit while marked. */
  markedBonusPct?: number
  /** Last Light: attack added on top of `attack` until `tempAttackUntil`. */
  tempAttackBonus?: number
  /** Last Light: absolute game-time the temporary attack bonus expires. */
  tempAttackUntil?: number
  /** Mirror Step: true for a reflection — draws fire, deals no damage, expires on its own. */
  isDecoy?: boolean
  /** Mirror Step: absolute game-time this decoy evaporates. */
  decoyExpiresAt?: number
  /** Cold Snap: ms remaining chilled — attack cooldown is divided by `chillAtkMult` while > 0. */
  chillTimer?: number
  /** Cold Snap: 0-1 attack-speed multiplier while chilled. */
  chillAtkMult?: number
  /** Cold Snap: 0-1 move-speed multiplier while chilled. */
  chillMoveMult?: number
  /** Deeproot: absolute game-time this unit last took damage. */
  lastDamagedAt?: number
  /** Deeproot: fractional HP carried between regen ticks. */
  regenAccum?: number
  /** Deeproot: HP as of the previous tick — a drop counts as a hit from any source. */
  lastSeenHp?: number
  /** Unbroken Vigil: lethal-save charges left on the warden. */
  vigilChargesLeft?: number
  /** Unbroken Vigil: true once this unit has been saved — it cannot be saved twice. */
  vigilSaved?: boolean
  /** True once this unit's death has been reported to the hero-ability hooks (Last Light
   *  fires once per fallen ally, not once per tick while the body lingers). */
  deathNotified?: boolean
}

// ─── Ground Hazards ───────────────────────────────────────

/** Persistent ground hazard (e.g. poison gas cloud) — damages units that move through it. */
export interface Hazard {
  id: string
  x: number
  y: number
  radius: number
  /** Damage per second dealt to enemies within radius. */
  dps: number
  owner: 'player' | 'opponent'
  /** Absolute ms game-time when this hazard expires. */
  expiresAt: number
}

// ─── Animation Events ─────────────────────────────────────

export type AnimEventKind = 'projectile' | 'hit' | 'aoe' | 'gascloud'

export interface AnimEvent {
  id: string
  kind: AnimEventKind
  /** ID of the unit that originated this event (used to align with visual jitter). */
  fromUnitId?: string
  /** Game-coordinate origin of the event. */
  fromX: number
  fromY: number
  /** Game-coordinate destination (projectiles travel toward this). */
  toX: number
  toY: number
  /** Absolute game-time (ms) when this event expires and should be removed. */
  expiresAt: number
  /** Visual style of the projectile — arrow, fireball, icebolt, etc. */
  projectileType?: ProjectileType
  /** For AOE projectiles: radius of the impact ring in px. */
  aoeRadius?: number
}

export interface Card {
  id: string
  name: string
  rarity: CardRarity
  cost: number
  cardType: CardType
  unit?: UnitTemplate
  upgradeEffect?: UpgradeEffect
  description: string
  lore?: string              // flavour text shown in the card detail view
  isHero?: boolean           // hero cards deploy a unit AND trigger a heroEffect buff
  heroEffect?: UpgradeEffect // the permanent buff applied to all friendly units when played
  /** Secret variant type — set on dynamically-generated shiny/holofoil/glass cards. */
  variant?: 'shiny' | 'holofoil' | 'glass'
  /** Glass cards: probability (0–1) the card shatters when played (unit instantly dies). */
  glassBreakChance?: number
  /** Augment cards: which equipment slot this occupies. */
  augmentSlot?: AugmentSlot
  /** Augment cards: base stat bonuses (scaled by instance level when equipped). */
  augmentEffect?: AugmentEffect
  /** Augment cards: which named set this belongs to. */
  setName?: string
}

// ─── Boss Trait State ─────────────────────────────────────

export interface BossTraitState {
  /** HP percentage thresholds that have already fired (e.g. [50, 33]). */
  firedThresholds: number[]
  /** gameTime (ms) of the last periodic trait activation. */
  lastTraitFireMs: number
  /** True once a game_time_gte trait has fired (once-only guard). */
  traitFired: boolean
  /** gameTime (ms) at which the pending landing effect resolves (burrow/fly/jump). */
  landingAtMs?: number
  /** Target X coordinate for the landing effect. */
  landX?: number
  /** Target Y coordinate for the landing effect. */
  landY?: number
  /** gameTime until which the opponent base cannot take damage. */
  baseInvulnerableUntilMs: number
  /** True once a split trait has fired; win requires all fragment units dead. */
  splitActive?: boolean
  /** IDs of live split fragment units (all must die to win). */
  splitUnitIds?: string[]
}

// ─── Pending Spell Cast (opponent AOE telegraph) ──────────

/** A damage-dealing AOE upgrade card the opponent has committed to (mana spent, card
 *  consumed) but whose damage application is deferred behind a visible windup + Counter QTE. */
export interface PendingSpellCast {
  /** Card name, for the telegraph banner and GameOver "Defeated by" callout. */
  cardName: string
  effect: Extract<UpgradeEffect, { type: 'aoe' }>
  /** gameTime (ms) when the cast was started. */
  startedAtMs: number
  /** gameTime (ms) when the damage resolves. */
  resolvesAtMs: number
  /** Damage cap, as a fraction (0–1) of the commander's max HP, frozen at the moment the
   *  player pressed Counter — scales linearly with how much of the windup had already
   *  elapsed (see COUNTER_DAMAGE_FLOOR_PCT). Absent (undefined) means no press was
   *  registered before resolution — the spell's full raw damage applies, uncapped. */
  counterPct?: number
}

// ─── Battle Events ────────────────────────────────────────

export type BattleEventType = 'bloodMoon' | 'fogOfWar' | 'supplyDrop' | 'earthquake'

export interface BattleEventState {
  type: BattleEventType
  label: string
  remainingMs: number        // duration; instant events use ~3000 just for banner display
}

// ─── Terrain ─────────────────────────────────────────────
// Types and constants owned by engine/terrain.ts; re-exported here for
// consumers that import from the top-level types module.

import type { TerrainType as _TerrainType, TerrainObstacle as _TerrainObstacle, RoadDef as _RoadDef, BattlefieldDecorItem as _BattlefieldDecorItem } from './engine/terrain'
export type { TerrainType, TerrainObstacle, RoadDef, BattlefieldDecorItem } from './engine/terrain'
export { TERRAIN_AVOID_SHAPE, RUIN_FOOTPRINT_RADIUS } from './engine/terrain'
type TerrainType = _TerrainType
type TerrainObstacle = _TerrainObstacle
type RoadDef = _RoadDef
type BattlefieldDecorItem = _BattlefieldDecorItem

// ─── Game ────────────────────────────────────────────────

export interface Base {
  hp: number
  maxHp: number
}

export type GamePhase =
  | { type: 'playing' }
  | { type: 'celebration'; winner: 'player' }
  | { type: 'gameOver'; winner: 'player' | 'opponent' | 'draw' }
  | { type: 'fingerSmash'; wave: number; smashedNames: string[]; rewardDue: boolean }
  | { type: 'waveReward'; wave: number; smashedNames: string[] }

export const LANE_WIDTH = 500

/** Fixed width/height ratio the battlefield always renders at (production and the
 *  battlefield editor) — matches a modern phone's portrait shape (e.g. 390x844).
 *  The battlefield letterboxes (centers with dead space) rather than stretching
 *  to fill wider/shorter containers. See web/src/hooks/useLetterboxSize.ts. */
export const BATTLEFIELD_ASPECT_RATIO = 9 / 19.5

/** Fixed width/height ratio the LANE — the play area between the frame's reserved
 *  HUD bands — is letterboxed to, the same way the frame itself is. The bands are
 *  fixed pixel heights, so without this the lane's shape would drift with the
 *  frame's (0.62 tall-desktop .. 0.72 small-phone), and terrain art (square tiles
 *  sized from lane HEIGHT) would lay a different number of tile columns across the
 *  lane on every device than the collision grid assumes. Locking the shape is what
 *  lets a collision tile and a drawn tile be the exact same pixels — see
 *  GRID_REF_WIDTH in game/engine/terrainGrid.ts, which derives from this.
 *
 *  544/845 = exactly 17 tile columns, chosen by measuring the letterbox loss over
 *  frame heights 700-1400: ~10% of width at the smallest, ~8% of height at the
 *  tallest, and near zero through the phone range. */
export const LANE_ASPECT_RATIO = 544 / 845

/** In endless mode, player structures may not be placed beyond this forward x-coordinate (3 rows from base). */
export const ENDLESS_STRUCTURE_MAX_X = 60

export type OpponentStrategy = 'swarm' | 'turtle' | 'rush'

export interface BattleStats {
  cardsPlayed: Record<string, number>  // card name → times played this battle
  playerKills: number                   // opponent mobile units destroyed
  playerUnitsLost: number              // player mobile units destroyed
}

export interface GameState {
  playerBase: Base
  opponentBase: Base
  field: Unit[]
  playerHand: Card[]
  playerDeck: Card[]
  opponentHand: Card[]
  opponentDeck: Card[]
  mana: number
  maxMana: number
  manaAccum: number          // fractional mana toward next point (0–1)
  log: string[]
  phase: GamePhase
  opponentTimer: number      // ms until opponent next acts
  opponentIntervalMs: number // ms between opponent turns (scales with difficulty)
  opponentStrategy: OpponentStrategy
  gameTime: number           // total elapsed game time in ms
  playerScore: number        // cumulative damage dealt to opponent
  opponentScore: number      // cumulative damage dealt to player
  suddenDeath: boolean       // true once all cards exhausted
  suddenDeathTimer: number   // ms remaining in sudden death (60 000 at start)
  suddenDeathBuildingTimer: number  // ms until next building damage tick (2 000 interval)
  battleEventTimer: number   // ms until next battle event fires
  activeBattleEvent: BattleEventState | null
  bossAI?: string            // 'thornlord' etc. — drives boss-specific opponent logic
  bossCard?: string          // card name of the phase-2 boss unit
  bossName?: string          // display name for the boss (e.g. 'The Thornlord')
  bossCardActive?: boolean   // true once the phase-2 unit has been deployed
  bossHpMultiplier?: number  // HP multiplier applied when boss card spawns (default 10)
  /** Live state for the active boss's trait system. Undefined when not in a boss fight. */
  bossTraitState?: BossTraitState
  terrain: TerrainObstacle[]
  roads?: RoadDef[]          // act/node-authored road paths for the battlefield
  roadFollowing?: boolean    // when true, mobile units path onto and follow `roads` toward the enemy base (see engine/units.ts)
  environment?: string       // battlefield background theme ('forest' | 'ruins' | 'camp' | 'citadel' | 'ashen')
  decor?: BattlefieldDecorItem[]  // act/node-authored manual decor placements; replaces the procedural decor scatter when non-empty
  /** When true, unit movement enforces hard tile-based blocking from `terrain`/`roads`
   *  (see game/engine/terrainGrid.ts) instead of the legacy soft lateral avoidance.
   *  Set only by the battlefield editor's "validate on save" step — defaults to
   *  false/undefined for all existing acts/nodes, so this is zero behavior change
   *  until an act/node explicitly opts in. */
  terrainValidated?: boolean
  /** Lazily-built, battle-lifetime cache of terrainGrid.ts's obstacle/road tile
   *  maps — built once (not per-tick) since `terrain`/`roads` don't change
   *  mid-battle. Only populated when `terrainValidated` is true. */
  terrainGridCache?: {
    obstacleTiles: Map<string, { type: TerrainType; zIndex: number }>
    roadTiles: Map<string, number>
    /** Flow fields used to route blocked units around terrain, keyed by
     *  `profile|swims|goalX`. Built on first use, not upfront — most battles
     *  only ever need the two ground fields. See buildFlowField. */
    flowFields: Map<string, Map<string, number>>
  }
  unitReviveHp?: 'half' | 'full' | number      // pending one-time unit revive at this HP value (set by Soulstone/Salvage Hook relics)
  relicManaBonus?: number             // passive +N to maxMana cap (set by Prism Lens relic)
  playerAtkMult?: number              // ATK multiplier applied to all player units, incl. later deploys (Glass Cannon Protocol exotic)
  phantomReviveChance?: number        // 0–1 chance each dying player unit revives at 50% HP (Phantom Legion exotic)
  manaOverflowCap?: number            // mana keeps regenerating up to maxMana + this (Mana Surge exotic)
  farmSpawnsGoblin?: boolean          // building a Farm also spawns a Goblin (The Last Farm exotic)
  structureDrawsCard?: boolean        // building any structure draws 1 extra card (The Architect's Eye exotic)
  playerManaRegenMs?: number          // player's upgraded mana regen interval (default 3000 ms)
  tickEffects?: TickEffect[]          // periodic effects applied each engine tick
  onCardPlayedEffects?: OnCardPlayedEffect[]  // effects applied when the player plays a card
  bossSpawnKillPct?: number           // Fraction of player units killed by boss shockwave (0.5 base, scales with run count)
  battleStats: BattleStats
  /** Transient animation events (projectiles, hit flashes). Cleared each tick after expiry. */
  animEvents: AnimEvent[]
  /** Persistent blood pool stains left by fallen units. FIFO-capped at 25; older pools fade out. */
  bloodPools: Array<{ id: string; x: number; y: number; fadingAt?: number }>
  /** Active ground hazards (e.g. gas clouds) — damage units within radius while alive. */
  hazards: Hazard[]
  /** True when running in Endless Mode (wave survival). */
  endlessMode?: boolean
  /** Current wave number in Endless Mode (starts at 1). */
  endlessWave?: number
  /** Survival time in ms (Endless Mode only). */
  endlessSurvivalMs?: number
  /** Cached player deck template for infinite card draw. */
  endlessPlayerDeckTemplate?: Card[]
  /** Cached opponent deck template for wave respawning. */
  endlessOpponentDeckTemplate?: Card[]
  /** Remaining ms of wave-start truce (opponent base invulnerable while > 0). */
  endlessWaveTruceMs?: number
  /** Mana multiplier applied to opponent each turn; increases per wave. */
  endlessOpponentManaMult?: number
  /** Allow the player to have unlimited maximum mana. */
  forgiveManaLimit?: boolean
  /** Daily challenge: floor for maxMana equal to the highest card cost in the player's deck. */
  deckMaxMana?: number
  /** Player unit movement stance. Defaults to 'auto' (original behaviour). */
  playerStance?: 'auto' | 'attack' | 'hold' | 'defend'
  /** Rules governing stance availability; undefined = no restrictions (current behaviour). */
  stanceRules?: StanceRules
  /** gameTime ms at which the current non-auto stance auto-expires. */
  stanceActiveUntil?: number
  /** gameTime ms before which no non-auto stance can be activated (cooldown). */
  stanceCooldownUntil?: number
  /** Boss HP value at which phase 2 triggers (boss battles only — boss spawned at game start). */
  bossPhase2Hp?: number
  /** Names of secret-rare cards obtained during this game session (flushed to collection on win/end). */
  secretRaresObtained?: string[]
  /** Count of glass cards that shattered on play this session. */
  glassShatterCount?: number
  /** Archetype passive applied for this battle (copied from RunState at battle start). */
  archetypePassive?: Archetype
  /**
   * Extra mana floor granted to the opponent at tier 4+ (see effectiveTier in
   * campaignHelpers.ts) — read by opponentAI.ts. Absent/0 outside campaign
   * battles, so Quick Battle and other newGame() callers are unaffected.
   */
  opponentManaFloorBonus?: number
  /**
   * Raises the opponent's per-turn card-play ceiling at tier 5 — read by
   * opponentAI.ts. Absent outside campaign battles.
   */
  opponentMaxPlaysOverride?: number
  /**
   * Deck-power effective tier (1-5, see effectiveTier in campaignHelpers.ts)
   * for this battle. Read by opponentAI.ts to gate its tier-scaled behaviour
   * (#2295) — field-aware card bias at tier 3+, playing its best affordable
   * card first at tier 4+, and committing to every interval's full play
   * budget at tier 5. Absent outside campaign battles, so Quick Battle,
   * endless mode, and the daily/weekly challenges are unaffected.
   */
  opponentTier?: number
  /** Current ATK multiplier applied to player mobile units by the Swarm Tactician passive. */
  swarmAtkMult?: number
  /** Live opponent AOE-spell cast in its windup window. Null when no cast is pending. */
  pendingSpellCast: PendingSpellCast | null
  /** Source of the most recent damage dealt to the player's commander — drives the
   *  GameOver "Defeated by" callout. Never cleared mid-battle, so it always reflects
   *  the final blow once the battle ends. */
  lastPlayerDamageSource?: { kind: 'spell'; name: string } | { kind: 'unit'; name: string }
}

export interface StanceRules {
  allowed: Array<'auto' | 'attack' | 'hold' | 'defend'>
  /** How long a non-auto stance lasts before auto-reverting to 'auto' (ms). Omit for no limit. */
  durationMs?: number
  /** How long the player must wait after a stance expires before activating another (ms). */
  cooldownMs?: number
}
